import { useState, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCheck, faX } from '@fortawesome/free-solid-svg-icons';
import secureAxios from '../../utils/secureAxios';
import { secureFetch } from '../../utils/secureFetch';
import Header from '../../components/Header/Header';
import NavInf from '../../components/NavInf/NavInf';
import Footer from '../../components/Footer/Footer';
import { useNavigate } from 'react-router-dom';
import { isEmbedded } from '../../utils/isEmbedded';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { ClipLoader } from 'react-spinners';
import EmbeddedLayout from '../../components/EmbeddedLayout/EmbeddedLayout';
import { getLoginRoute } from '../../utils/navigationHelper';
import './ProviderProfile.css';

const API_BASE = 'https://api.minymol.com';

const ProviderProfile = () => {
    const navigate = useNavigate();
    const logoInputRef = useRef();
    const bannerInputRef = useRef();

    const [loading, setLoading] = useState(true);
    const [providerInfo, setProviderInfo] = useState(null);
    const [logo, setLogo] = useState(null);
    const [banner, setBanner] = useState(null);
    const [logoTemp, setLogoTemp] = useState(null);
    const [bannerTemp, setBannerTemp] = useState(null);

    const [cropType, setCropType] = useState(null);
    const [cropData, setCropData] = useState({
        image: null,
        crop: { x: 0, y: 0 },
        zoom: 1,
        aspect: 1,
    });
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [activeTab, setActiveTab] = useState('banner'); // 'banner' o 'logo'

    // Efecto para manejar autenticación y cargar datos del proveedor
    useEffect(() => {
        const fetchProviderData = async () => {
            try {
                setLoading(true);
                const res = await secureFetch(`${API_BASE}/providers/me`);
                
                if (!res.ok) {
                    throw new Error('Error al obtener datos del proveedor');
                }
                
                const data = await res.json();
                setProviderInfo(data);
                setLogo(data.logo_url || null);
                setBanner(data.banner_url || null);
                
                // Actualizar localStorage con los datos más recientes
                const usuario = JSON.parse(localStorage.getItem('usuario'));
                if (usuario) {
                    usuario.proveedorInfo = {
                        ...usuario.proveedorInfo,
                        logo_url: data.logo_url,
                        banner_url: data.banner_url,
                        nombre_empresa: data.nombre_empresa
                    };
                    localStorage.setItem('usuario', JSON.stringify(usuario));
                }
            } catch (error) {
                console.error('Error al cargar datos del proveedor:', error);
            } finally {
                setLoading(false);
            }
        };

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                fetchProviderData();
            } else {
                // Si no hay usuario autenticado, redirigir a login
                console.log('❌ No hay usuario autenticado, redirigiendo a login');
                setLoading(false);
                navigate(getLoginRoute());
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const handleImageSelect = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        const aspectRatio = type === 'banner' ? 6 / 1 : 1 / 1;
        reader.onload = () => {
            setCropType(type);
            setCropData({
                image: reader.result,
                crop: { x: 0, y: 0 },
                zoom: 1,
                aspect: aspectRatio,
            });
        };
        reader.readAsDataURL(file);
    };

    const onCropComplete = (_, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const confirmCrop = async () => {
        const cropped = await getCroppedImg(cropData.image, croppedAreaPixels);
        const previewUrl = URL.createObjectURL(cropped);

        if (cropType === 'logo') setLogoTemp({ blob: cropped, url: previewUrl });
        if (cropType === 'banner') setBannerTemp({ blob: cropped, url: previewUrl });

        setCropType(null);
    };

    const guardarCambios = async () => {
        if (!logoTemp && !bannerTemp) return;

        try {
            if (logoTemp) {
                const mime = logoTemp.blob.type;
                const presigned = await secureAxios.get(`${API_BASE}/providers/me/presigned-url`, {
                    params: { type: 'logo', mime },
                });
                await axios.put(presigned.data.url, logoTemp.blob, {
                    headers: { 'Content-Type': mime },
                });
                await secureAxios.patch(`${API_BASE}/providers/me/update-imagenes`, {
                    logoUrl: presigned.data.finalUrl,
                });
                setLogo(presigned.data.finalUrl);
                setLogoTemp(null);
                
                // Actualizar localStorage
                const usuario = JSON.parse(localStorage.getItem('usuario'));
                if (usuario?.proveedorInfo) {
                    usuario.proveedorInfo.logo_url = presigned.data.finalUrl;
                    localStorage.setItem('usuario', JSON.stringify(usuario));
                }
            }

            if (bannerTemp) {
                const mime = bannerTemp.blob.type;
                const presigned = await secureAxios.get(`${API_BASE}/providers/me/presigned-url`, {
                    params: { type: 'banner', mime },
                });
                await axios.put(presigned.data.url, bannerTemp.blob, {
                    headers: { 'Content-Type': mime },
                });
                await secureAxios.patch(`${API_BASE}/providers/me/update-imagenes`, {
                    bannerUrl: presigned.data.finalUrl,
                });
                setBanner(presigned.data.finalUrl);
                setBannerTemp(null);
                
                // Actualizar localStorage
                const usuario = JSON.parse(localStorage.getItem('usuario'));
                if (usuario?.proveedorInfo) {
                    usuario.proveedorInfo.banner_url = presigned.data.finalUrl;
                    localStorage.setItem('usuario', JSON.stringify(usuario));
                }
            }
        } catch (error) {
            console.error('Error al guardar cambios:', error);
            alert('Error al guardar los cambios. Por favor, intenta de nuevo.');
        }
    };

    // Mostrar loading mientras se verifica autenticación
    if (loading) {
        return (
            <>
                {!isEmbedded() && (
                    <Header
                        minimal={true}
                        providerName={providerInfo?.nombre_empresa}
                        menuProvider={true}
                        currentPage='store'
                    />
                )}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', width: '100%' }}>
                    <ClipLoader color="#fa7e17" size={30} />
                </div>
                {!isEmbedded() && <NavInf />}
                {!isEmbedded() && <Footer />}
            </>
        );
    }

    return (
        <>
            {!isEmbedded() && (
                <Header
                    minimal={true}
                    providerName={providerInfo?.nombre_empresa}
                    menuProvider={true}
                    currentPage='store'
                />
            )}

            {/* Layout embebido (sidebar + header) cuando está en iframe */}
            {isEmbedded() && <EmbeddedLayout />}

            <div className="provider-profile">
                <div className="profile-header">
                    <h1>Personaliza tu Tienda</h1>
                    <p>Sube tu logo y banner para darle identidad a tu catálogo</p>
                </div>

                {/* Tabs */}
                <div className="tabs-container">
                    <button 
                        className={`tab ${activeTab === 'banner' ? 'active' : ''}`}
                        onClick={() => setActiveTab('banner')}
                    >
                        Banner
                    </button>
                    <button 
                        className={`tab ${activeTab === 'logo' ? 'active' : ''}`}
                        onClick={() => setActiveTab('logo')}
                    >
                        Logo
                    </button>
                </div>

                {/* Tab Content */}
                <div className="tab-content">
                    {activeTab === 'banner' && (
                        <div className="upload-card">
                            <div className="card-info">
                                <h2>Banner de tu Tienda</h2>
                                <p className="card-description">
                                    El banner aparecerá en la parte superior de tu catálogo. 
                                    Usa una imagen horizontal de alta calidad.
                                </p>
                                <span className="card-specs">Dimensiones recomendadas: 1200x200px</span>
                            </div>
                            
                            <div className="upload-area">
                                <label className='banner-upload'>
                                    {bannerTemp?.url || banner ? (
                                        <img src={bannerTemp?.url || banner} className='preview-image' alt='Banner' />
                                    ) : (
                                        <div className="upload-placeholder">
                                            <FontAwesomeIcon icon={faPlus} className='upload-icon' />
                                            <span className="upload-text">Haz clic para subir tu banner</span>
                                            <span className="upload-hint">JPG, PNG o WEBP</span>
                                        </div>
                                    )}
                                    <input
                                        type='file'
                                        accept='image/*'
                                        onChange={(e) => handleImageSelect(e, 'banner')}
                                        hidden
                                        ref={bannerInputRef}
                                    />
                                </label>
                            </div>

                            {(bannerTemp?.url || banner) && (
                                <button className='change-btn' onClick={() => bannerInputRef.current.click()}>
                                    Cambiar Banner
                                </button>
                            )}
                        </div>
                    )}

                    {activeTab === 'logo' && (
                        <div className="upload-card">
                            <div className="card-info">
                                <h2>Logo de tu Tienda</h2>
                                <p className="card-description">
                                    Tu logo representa tu marca. Usa una imagen cuadrada con fondo 
                                    transparente para mejores resultados.
                                </p>
                                <span className="card-specs">Dimensiones recomendadas: 400x400px</span>
                            </div>
                            
                            <div className="upload-area">
                                <label className='logo-upload'>
                                    {logoTemp?.url || logo ? (
                                        <img src={logoTemp?.url || logo} className='preview-image' alt='Logo' />
                                    ) : (
                                        <div className="upload-placeholder">
                                            <FontAwesomeIcon icon={faPlus} className='upload-icon' />
                                            <span className="upload-text">Haz clic para subir tu logo</span>
                                            <span className="upload-hint">PNG con fondo transparente recomendado</span>
                                        </div>
                                    )}
                                    <input
                                        type='file'
                                        accept='image/*'
                                        onChange={(e) => handleImageSelect(e, 'logo')}
                                        hidden
                                        ref={logoInputRef}
                                    />
                                </label>
                            </div>

                            {(logoTemp?.url || logo) && (
                                <button className='change-btn' onClick={() => logoInputRef.current.click()}>
                                    Cambiar Logo
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {(logoTemp || bannerTemp) && (
                    <div className="save-section">
                        <button className="save-changes-btn" onClick={guardarCambios}>
                            <FontAwesomeIcon icon={faCheck} />
                            Guardar Cambios
                        </button>
                    </div>
                )}

                {cropType && (
                    <div className="cropper-modal">
                        <Cropper
                            image={cropData.image}
                            crop={cropData.crop}
                            zoom={cropData.zoom}
                            aspect={cropData.aspect}
                            onCropChange={(crop) => setCropData({ ...cropData, crop })}
                            onZoomChange={(zoom) => setCropData({ ...cropData, zoom })}
                            onCropComplete={onCropComplete}
                        />
                        <div className="cropper-controls">
                            <button onClick={confirmCrop}>
                                <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button onClick={() => setCropType(null)}>
                                <FontAwesomeIcon icon={faX} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {!isEmbedded() && <NavInf />}
            {!isEmbedded() && <Footer />}
        </>
    );
};

export default ProviderProfile;
